/**
 * buildingPlan — the pure, deterministic building grammar. No engine trig, no
 * Math.random: integer module-grid massing + quarter-turn rotations only (the
 * one trig consumer, planDoorSide, routes through pmath — pmath-ban safe).
 * Layer seeds keep massing / palette / greebles / wear / wildcard independent:
 * re-tuning one layer never reshuffles another on existing worlds.
 *
 * Rot convention (BINDING for all later tasks): rot = quarter-turns about y
 * carrying the module's local +z face to the world side. Sides: 0 = +z,
 * 1 = −z, 2 = +x, 3 = −x. Door module rot per side: 0→0, 1→2, 2→1, 3→3.
 */
import { mulberry32, type Rng } from "../rng.js";
import { hashInts } from "../worldgen/hash.js";
import * as pmath from "../pmath.js";
import { MODULE_UNIT_M, LEVEL_H_M } from "./kit.js";
import { RECIPES, type Recipe, type GreebleEntry } from "./recipes.js";

export type BuildingArchetype =
  "habitation" | "watchtower" | "industrial" | "lab" | "marketplace" | "outpost";
export const BUILDING_ARCHETYPES: readonly BuildingArchetype[] =
  ["habitation", "watchtower", "industrial", "lab", "marketplace", "outpost"];

export interface PlanInput {
  /** Authored one-storey footprints, selected only by program generator 3. */
  footprint?: 'wing' | 'court';
  archetype: BuildingArchetype;
  seed: number;              // uint32 buildingSeed (record field 14)
  wM: number; dM: number;    // footprint extents, metres (record fields 4, 5)
  hM: number;                // height budget, metres (record field 6)
  doorSide: 0 | 1 | 2 | 3;   // 0=+z 1=−z 2=+x 3=−x in the building local frame
  /** Interiors P1: generate the GROUND floor as a hollow walk-in shell (per-
   * face wall panels with a carved doorway, floor + roof slabs) instead of
   * solid module blocks. Derived from record field 15 via
   * interior.ts::interiorEnabledUse at BOTH call sites (bake + collision).
   * Consumes NO rng — massing/trim/greeble streams are byte-identical either
   * way; only `collision` and the new `shell` output change. Default false. */
  hollow?: boolean;
  /** Ground spread under the footprint (record field 17, metres): raises the
   * interior floor slab so terrain never pokes through. Default 0. */
  floorRaiseM?: number;
}

/** Local frame: x = record width axis, y = up, z = depth axis; origin = grounded
 *  base centre. Positions in METRES. rot = quarter-turns about y. */
export interface ModulePlacement {
  module: string; x: number; y: number; z: number; rot: 0 | 1 | 2 | 3;
  level: number; gx: number; gz: number; wU: number; dU: number;
}
export interface TrimPlacement { piece: string; x: number; y: number; z: number; rot: 0 | 1 | 2 | 3 }
export interface GreeblePlacement { greeble: string; x: number; y: number; z: number; rot: 0 | 1 | 2 | 3 }
/** `wall: true` marks a box that must fold NON-standable (a shell wall panel /
 *  door flank / header): a standable box is walk-through in its top
 *  ROOF_STEP_M band (boxColliders.ts), which for a wall means phasing through
 *  it near the ceiling. Floors/roofs/solid modules omit it (standable). */
export interface LocalBox { x: number; y: number; z: number; w: number; h: number; d: number; wall?: boolean }

// ── interiors P1: the walk-in shell (ground floor) ───────────────────────────

/** Shell wall thickness (m) — matches the vault theme's doubled masonry read. */
export const SHELL_WALL_T = 0.4;
/** Interior floor slab BASE thickness (m). The slab's actual top is
 *  {@link ShellPlan.floorTopM} — raised by the record's ground spread (field
 *  17) so terrain never pokes through the interior floor. */
export const SHELL_FLOOR_T = 0.4;
/** Interior floor top safety ceiling (m above base). Interior ELIGIBILITY is
 *  gated at spread ≤ 1.0 (interior.ts INTERIOR_MAX_SPREAD_M), so the raise
 *  formula (SHELL_FLOOR_T + spread + 0.25) tops out at 1.65 in practice —
 *  this cap only guards a mis-wired caller. The slab is AUTHORITATIVE ground
 *  indoors (SettlementColliders.interiorFloorRadiusAt → clampToGround), so
 *  it must clear the carved pad everywhere; the 0.25 margin covers interior
 *  terrain bumps above the corner max. */
export const SHELL_FLOOR_TOP_MAX = 1.75;
/** Roof slab thickness (m). Slab TOP stays at the module top (LEVEL_H_M ×
 *  storeys) so roof standing height is unchanged from the solid fold. */
export const SHELL_ROOF_T = 0.3;
/** Doorway width (m). 1.2 → 1.6 (P2 fix pass): 1.2 left 0.15 m of clearance
 *  per side around the 0.45 m body radius — entering felt sticky; 1.6 gives
 *  0.35 m per side. (The 1.2 contract remains the HOMESTEAD piece scale.) */
export const SHELL_DOOR_W = 1.6;
/** Doorway header BOTTOM (m above base) — FIXED, not sill-relative, so the
 *  visual door piece (authored once) and the collision header always agree
 *  regardless of the per-building floor raise. Clear opening above the
 *  tallest sill: 3.2 − 1.0 = 2.2 m. */
export const SHELL_DOOR_HEAD_M = 3.2;

// ── interiors P3: multi-floor shells ─────────────────────────────────────────

/** Interior stair (P3): 18 collision treads climbing one full storey. */
export const SHELL_STAIR_TREADS = 18;
/** Stair run (m) along the cell. 3.4 → 3.6 + the ascent offset below
 *  (2026-07-30): the top tread must reach the CELL EDGE — it used to stop
 *  0.3 m short, leaving a gap over the stairwell opening that made players
 *  "jump to get onto the next floor". */
export const SHELL_STAIR_RUN_M = 3.6;
/** Ascent-direction shift of the run within its cell: treads span
 *  [−1.6, +2.0] — flush with the arrival edge, 0.4 m clear at the foot. */
export const SHELL_STAIR_OFFSET_M = 0.2;
/** Stair width (m) across the run. */
export const SHELL_STAIR_W_M = 1.2;
/** Ramp tilt from horizontal = atan(LEVEL_H_M / SHELL_STAIR_RUN_M) — a
 *  precomputed literal (pmath ban: no Math.atan in shared/). Rechecked by
 *  buildings-kit.test.mts against the run/rise pair. */
export const SHELL_STAIR_TILT_RAD = 0.866302262552679;
/** Ramp slab length = hypot(run, rise) — precomputed literal. */
export const SHELL_STAIR_LEN_M = 5.2497618993626745;

/** One per-cell-edge wall panel of a hollow storey. (x, z) is the CELL
 *  centre (metres, building-local); the panel sits on the `side` edge, inset
 *  fully inside the footprint. rot for the bake = SIDE_ROT[side]. */
export interface ShellWall { x: number; z: number; side: 0 | 1 | 2 | 3; kind: "solid" | "win" | "door" }
/** An interior stair: centreline at (x, z) (already offset against its wall),
 *  climbing from `level` to `level + 1`; rot carries local +z to the ASCENT
 *  direction. The cell above it keeps a stairwell OPENING (no floor slab). */
export interface ShellStair { x: number; z: number; level: number; rot: 0 | 1 | 2 | 3 }
export interface ShellPlan {
  walls: ShellWall[];
  /** Occupied level-0 cells (floor slab per cell). */
  floors: { x: number; z: number }[];
  /** P3: exposed cells per level (roof/terrace slab per cell; level 0 for a
   *  single-storey area, higher for terraces and the top roof). */
  caps: { x: number; z: number; level: number }[];
  /** P3: hollow upper-storey walls (kind solid|win — no doors above ground). */
  upperWalls: (ShellWall & { level: number })[];
  /** P3: upper floor slabs [L·H − 0.3, L·H] per occupied cell — each is both
   *  level L's floor and level L−1's ceiling. Stairwell cells are SKIPPED. */
  upperFloors: { x: number; z: number; level: number }[];
  /** P3: interior stairs, one per level transition. */
  stairs: ShellStair[];
  /** P3: false for specialty-massing archetypes (watchtower / outpost — their
   *  shafts, heads and hull rings are not box rooms): level 0 shells, upper
   *  storeys stay SOLID modules, no interior stairs. */
  multiFloor: boolean;
  /** Interior floor slab TOP (m above base): SHELL_FLOOR_T + the record's
   *  ground spread + margin, capped at SHELL_FLOOR_TOP_MAX — high enough that
   *  the carved terrain under the footprint stays below the slab. */
  floorTopM: number;
}

export interface BuildingPlan {
  archetype: BuildingArchetype; seed: number; unit: number;
  cellsW: number; cellsD: number; levels: number;
  modules: ModulePlacement[]; trims: TrimPlacement[]; greebles: GreeblePlacement[];
  door: { x: number; z: number; side: 0 | 1 | 2 | 3; wM: number; hM: number };
  entry: "seamless" | "transition";
  paletteIndex: number;      // 0..3 into the archetype ramp (client tint)
  wildcard: string | null;   // greeble id or null
  collision: LocalBox[];     // module AABBs (solid) / shell boxes (hollow level 0)
  /** Hollow ground-floor layout (interiors P1); null for solid plans. */
  shell: ShellPlan | null;
}

export const LAYER_MASSING = 0; export const LAYER_PALETTE = 1;
export const LAYER_GREEBLE = 2; export const LAYER_WEAR = 3; export const LAYER_WILDCARD = 4;
/** Interiors P2 (interior.ts::interiorPlan): room partition + furnishing
 *  streams — appended layers on the same 0x51ab fan-out; existing layers
 *  never reshuffle (independent hashes per layer index). */
export const LAYER_ROOMS = 5; export const LAYER_FURNISH = 6;

/** Salt 0x51ab — building-plan layer fan-out (registered in worldgen/hash.ts). */
export function layerRng(seed: number, layer: number): Rng {
  return mulberry32(hashInts(seed >>> 0, 0x51ab, layer));
}

export interface Massing {
  cellsW: number; cellsD: number;
  /** one Uint8Array(cellsW*cellsD) per level; 1 = occupied */
  levels: Uint8Array[];
  /** Optional crown: a recipe may name ONE top-level cell (gx, gz) to carry a
   * `mod-dome` cap. tileMassing emits it as an extra 1×1 module at level index
   * `levels.length` (one above the top grid — plan.levels does NOT count it),
   * guarded on top-grid occupancy so the support invariant always holds. */
  domeCell?: [number, number];
}

/** Outward step per side: 0 = +z, 1 = −z, 2 = +x, 3 = −x. */
const SIDE_DX = [0, 0, 1, -1] as const;
const SIDE_DZ = [1, -1, 0, 0] as const;
/** rot (quarter-turns) that turns a piece's local +z face out of the given side.
 * Shared by the door module and every side-anchored trim (parapet edge / skirt /
 * stair) so all four use one BINDING side→rot map: 0→0, 1→2, 2→1, 3→3.
 * Exported (interiors P1): the shell bake anchors wall panels by side. */
export const SIDE_ROT = [0, 2, 1, 3] as const;
/** Adjacent side pairs (one z-side + one x-side) → the 4 convex corners. */
const CORNER_PAIRS = [[0, 2], [0, 3], [1, 2], [1, 3]] as const;
/** rot → facing direction (the rotated local +z): inverse of SIDE_ROT.
 *  Lives here (not interior.ts) because the shell's stair placement needs it;
 *  interior.ts re-exports for its consumers. */
export const ROT_DX = [0, 1, 0, -1] as const;
export const ROT_DZ = [1, 0, -1, 0] as const;

export function buildingPlan(input: PlanInput): BuildingPlan {
  // Cell clamp 6 → 8 (2026-07-30 bigger-buildings pass, follows the 34 m
  // footprint cap) → 11 (2026-08-02 civic wave, follows the 44 m landmark cap).
  const cellsW = Math.max(1, Math.min(11, Math.floor(input.wM / MODULE_UNIT_M)));
  const cellsD = Math.max(1, Math.min(11, Math.floor(input.dM / MODULE_UNIT_M)));
  // Level clamp 5 → 14 (2026-08-02): the civic landmark uses carry height
  // budgets up to ~60 m — the old clamp silently flattened every landmark to
  // 20 m, which is why the height multipliers never showed on screen. The
  // recipes themselves decide how much of the budget to climb.
  const maxLevels = Math.max(1, Math.min(14, Math.round(input.hM / LEVEL_H_M)));
  const rng = layerRng(input.seed, LAYER_MASSING);
  const recipe = RECIPES[input.archetype];
  let massing = recipe.massing(rng, cellsW, cellsD, maxLevels);
  if (input.footprint) {
    if (maxLevels !== 1 || cellsW < 3 || cellsD < 3) throw new Error('Composed footprints require one storey and at least 12 × 12 metres');
    const grid = new Uint8Array(cellsW * cellsD).fill(1);
    const mirrored = (input.seed & 4) !== 0;
    const wingWidth = Math.max(1, Math.floor(cellsW / 3));
    for (let z = 0; z < cellsD; z++) for (let x = 0; x < wingWidth; x++) {
      const remove = input.footprint === 'wing' ? z < Math.ceil(cellsD / 2) : z > 0 && z < cellsD - 1;
      if (remove) grid[z * cellsW + (mirrored ? x : cellsW - 1 - x)] = 0;
    }
    massing = { cellsW, cellsD, levels: [grid] };
  }
  // Hardening: an empty massing (no levels, or a level-0 grid with no occupied
  // cell) would leave tileMassing with zero modules and crash doorFor. Degrade
  // to the 1×1 fallback hut — the same shape tiny footprints take — instead.
  if (massing.levels.length === 0 || !massing.levels[0].some((v) => v === 1)) {
    const hutGrid = new Uint8Array(cellsW * cellsD);
    hutGrid[((cellsD - 1) >> 1) * cellsW + ((cellsW - 1) >> 1)] = 1; // centre cell
    massing = { cellsW, cellsD, levels: [hutGrid] };
  }
  const modules = tileMassing(rng, massing, recipe, input.doorSide);
  const trims = trimPass(massing, modules); // Task 4
  const door = doorFor(modules, input.doorSide);
  const entry = massing.levels.length === 1 && modules.length === 1 ? "seamless" : "transition";
  if (entry === "seamless") modules[0] = { ...modules[0], module: "mod-box-door-hollow" };
  // Task 5: greebles + wildcard draw from their OWN layer streams, never the
  // massing stream — re-tuning the greeble table can't reshuffle massing.
  const { greebles, wildcard } = greeblePass(
    layerRng(input.seed, LAYER_GREEBLE), layerRng(input.seed, LAYER_WILDCARD),
    massing, modules, recipe, input.doorSide);
  // Interiors P1: hollow plans compute the ground-floor shell (pure function
  // of massing + modules + doorSide — no rng) and derive their collision from
  // it; solid plans keep the historical one-box-per-module fold byte-exact.
  const shell = input.hollow === true
    ? shellFor(massing, modules, input.doorSide, input.floorRaiseM ?? 0,
      input.archetype !== "watchtower" && input.archetype !== "outpost") : null;
  return {
    archetype: input.archetype, seed: input.seed >>> 0, unit: MODULE_UNIT_M,
    cellsW, cellsD, levels: massing.levels.length,
    modules, trims, greebles, door, entry,
    paletteIndex: Math.floor(layerRng(input.seed, LAYER_PALETTE)() * 4),
    wildcard,
    collision: shell !== null ? shellCollisionBoxes(shell, modules) : collisionBoxes(modules),
    shell,
  };
}

/**
 * The hollow ground floor: per occupied level-0 CELL, a floor slab; per
 * exposed cell edge (same-level neighbour unoccupied), a wall panel — the
 * door module's door face becomes a "door" panel (flanks + header around the
 * 1.2 × 2.1 opening), cells tiled by `mod-box-win` read "win", everything
 * else "solid"; per level-0 cell with nothing above, a roof cap. Interior
 * cell-to-cell edges get NO wall — P1 ships one open hall per building
 * (rooms are P2's interiorPlan).
 */
function shellFor(massing: Massing, modules: ModulePlacement[],
  doorSide: 0 | 1 | 2 | 3, floorRaiseM: number, multiFloor: boolean): ShellPlan {
  const { cellsW, cellsD, levels } = massing;
  const nLevels = levels.length;
  const occ = (L: number, gx: number, gz: number): boolean =>
    L >= 0 && L < nLevels && gx >= 0 && gx < cellsW && gz >= 0 && gz < cellsD &&
    levels[L][gz * cellsW + gx] === 1;
  const cx = (gx: number): number => (gx - (cellsW - 1) / 2) * MODULE_UNIT_M;
  const cz = (gz: number): number => (gz - (cellsD - 1) / 2) * MODULE_UNIT_M;
  // Cell → occupying module id per level (the trimPass idiom): window read.
  const modAt = new Map<string, string>();
  for (const m of modules) {
    for (let iz = 0; iz < m.dU; iz++) for (let ix = 0; ix < m.wU; ix++) {
      modAt.set(`${m.level}:${m.gx + ix}:${m.gz + iz}`, m.module);
    }
  }
  const door = modules.find((m) => m.level === 0 &&
    (m.module === "mod-box-door" || m.module === "mod-box-door-hollow"));

  // P3: stairwells FIRST — one per level transition, in a cell occupied at
  // both L and L+1, nearest the entry door (then nearest the stair below so
  // upper flights stack sensibly). The chosen ascent direction must ARRIVE
  // at an upper cell edge whose neighbour is occupied at L+1 when possible,
  // so you step off the top tread into the upper room, not into a wall.
  const stairs: ShellStair[] = [];
  const stairCellAbove = new Set<string>(); // `${level}:${gx}:${gz}` — opening at this level
  let refGx = door?.gx ?? 0, refGz = door?.gz ?? 0;
  // Every (cell, wall-side, ascent-rot) combo is SCORED (2026-07-30 play
  // findings: "stairs go to nowhere upstairs / bottom faces a wall"):
  //   REQUIRED: the top tread arrives beside a cell occupied at L+1 — you
  //             step off into the upper room, never into a wall;
  //   +4 the BOTTOM approach cell (behind the first tread) is open at L;
  //   +2 the hugged side is a real wall at level L (exposed edge);
  //   +1 the cell differs from the stair below (no stacked flights);
  //   − distance from the reference (door, then the stair below) as a
  //     fractional tie-break. No combo with an occupied arrival → no stair
  //     (and no stairwell opening): a sealed empty attic beats a stair into
  //     a wall.
  let arrGx = -9, arrGz = -9; // previous flight's LANDING cell
  for (let L = 0; multiFloor && L + 1 < nLevels; L++) {
    let best: { gx: number; gz: number; side: 0 | 1 | 2 | 3; rot: 0 | 1 | 2 | 3; score: number } | null = null;
    for (let gz = 0; gz < cellsD; gz++) for (let gx = 0; gx < cellsW; gx++) {
      if (!occ(L, gx, gz) || !occ(L + 1, gx, gz)) continue;
      // Keep the ENTRY clear: the door cell AND the walk-in cell inside it —
      // a stair in the inside-door cell ran its flank right across the
      // doorway (seen in the 2026-07-30 roofline screenshots).
      if (L === 0 && door !== undefined && (
        (gx === door.gx && gz === door.gz) ||
        (gx === door.gx - SIDE_DX[doorSide] && gz === door.gz - SIDE_DZ[doorSide]))) continue;
      for (let s = 0 as 0 | 1 | 2 | 3; s < 4; s = (s + 1) as 0 | 1 | 2 | 3) {
        const rots: (0 | 1 | 2 | 3)[] = s <= 1 ? [1, 3] : [0, 2]; // ascent runs ALONG the hugged wall
        for (const r of rots) {
          const agx = gx + ROT_DX[r], agz = gz + ROT_DZ[r];
          if (!occ(L + 1, agx, agz)) continue; // REQUIRED: arrival opens into the upper room
          let score = 0;
          // Open bottom approach is required in all but last resort (2026-07-30
          // "stairways need a gap between their ends and the walls"): a closed
          // approach puts an exterior wall 0.4m from the stair's first tread.
          if (!occ(L, gx - ROT_DX[r], gz - ROT_DZ[r])) score -= 500;
          if (!occ(L, gx + SIDE_DX[s], gz + SIDE_DZ[s])) score += 2;   // hugging a real wall
          if (!(L > 0 && gx === refGx && gz === refGz)) score += 1;    // not stacked on the flight below
          // NEVER planted on the lower flight's LANDING (2026-07-30 "invisible
          // barrier at the top of stairs": the next flight's tall column mass
          // sat exactly where you step off the flight below). Hard penalty —
          // only chosen if it is the sole viable stair cell.
          if (L > 0 && gx === arrGx && gz === arrGz) score -= 1000;
          const dx = gx - refGx, dz = gz - refGz;
          score -= (dx * dx + dz * dz) / 256; // near the reference, as a tie-break
          if (best === null || score > best.score) best = { gx, gz, side: s, rot: r, score };
        }
      }
    }
    if (best === null) break; // no landing anywhere — leave the storey sealed
    const off = MODULE_UNIT_M / 2 - SHELL_WALL_T - SHELL_STAIR_W_M / 2;
    stairs.push({
      x: cx(best.gx) + SIDE_DX[best.side] * off,
      z: cz(best.gz) + SIDE_DZ[best.side] * off,
      level: L, rot: best.rot,
    });
    stairCellAbove.add(`${L + 1}:${best.gx}:${best.gz}`);
    refGx = best.gx; refGz = best.gz;
    arrGx = best.gx + ROT_DX[best.rot]; arrGz = best.gz + ROT_DZ[best.rot];
  }

  const walls: ShellWall[] = [];
  const floors: { x: number; z: number }[] = [];
  const caps: { x: number; z: number; level: number }[] = [];
  const upperWalls: (ShellWall & { level: number })[] = [];
  const upperFloors: { x: number; z: number; level: number }[] = [];
  const shellLevels = multiFloor ? nLevels : 1;
  for (let L = 0; L < shellLevels; L++) {
    for (let gz = 0; gz < cellsD; gz++) for (let gx = 0; gx < cellsW; gx++) {
      if (!occ(L, gx, gz)) continue;
      const x = cx(gx), z = cz(gz);
      if (L === 0) floors.push({ x, z });
      else if (!stairCellAbove.has(`${L}:${gx}:${gz}`)) upperFloors.push({ x, z, level: L });
      if (!occ(L + 1, gx, gz)) caps.push({ x, z, level: L });
      for (let s = 0 as 0 | 1 | 2 | 3; s < 4; s = (s + 1) as 0 | 1 | 2 | 3) {
        if (occ(L, gx + SIDE_DX[s], gz + SIDE_DZ[s])) continue; // interior edge
        const win = modAt.get(`${L}:${gx}:${gz}`) === "mod-box-win";
        if (L === 0) {
          const isDoor = door !== undefined && gx === door.gx && gz === door.gz && s === doorSide;
          walls.push({ x, z, side: s, kind: isDoor ? "door" : win ? "win" : "solid" });
        } else {
          upperWalls.push({ x, z, side: s, kind: win ? "win" : "solid", level: L });
        }
      }
    }
  }
  const floorTopM = Math.min(SHELL_FLOOR_TOP_MAX,
    SHELL_FLOOR_T + Math.max(0, floorRaiseM) + 0.25);
  return { walls, floors, caps, upperWalls, upperFloors, stairs, floorTopM, multiFloor };
}

/**
 * Collision for a hollow plan — P3: EVERY storey is a shell. Walls fold
 * `wall: true` (non-standable, or the ROOF_STEP_M top band phases bodies
 * through them); floor/terrace/roof slabs standable. Upper floor slabs span
 * [L·H − 0.3, L·H] — their TOPS are the old solid-module tops, and cap slabs
 * top out at (L+1)·H, so buildingPlanHeightM and every roof-stand height are
 * UNCHANGED vs the solid fold (the doorSide-invariance proof holds: caps come
 * from massing occupancy alone). Stairs fold as SHELL_STAIR_TREADS standable
 * treads climbing one storey; the dome cap stays a solid block.
 */
function shellCollisionBoxes(shell: ShellPlan, modules: ModulePlacement[]): LocalBox[] {
  const U = MODULE_UNIT_M, H = LEVEL_H_M, T = SHELL_WALL_T;
  const out: LocalBox[] = [];
  // Contiguity MERGE (P3 box budget): adjacent cells' slabs — and adjacent
  // non-door wall panels below — are coplanar axis-aligned boxes; folding
  // them as single strips roughly halves the shell's collider count without
  // changing the solid geometry. Merge runs along x per (z row, y band).
  const mergeCells = (cells: { x: number; z: number }[], y: number, h: number): void => {
    const rows = new Map<number, number[]>();
    for (const c of cells) {
      const r = rows.get(c.z) ?? [];
      r.push(c.x);
      rows.set(c.z, r);
    }
    for (const [z, xsRaw] of [...rows.entries()].sort((a, q) => a[0] - q[0])) {
      const xs = xsRaw.sort((a, q) => a - q);
      let start = xs[0], prev = xs[0];
      const flush = (end: number): void => {
        out.push({ x: (start + end) / 2, y, z, w: end - start + U, h, d: U });
      };
      for (let i = 1; i < xs.length; i++) {
        if (xs[i] - prev > U + 1e-6) { flush(prev); start = xs[i]; }
        prev = xs[i];
      }
      flush(prev);
    }
  };
  mergeCells(shell.floors, 0, shell.floorTopM);
  for (let L = 1; L < 8; L++) {
    const lv = shell.upperFloors.filter((f) => f.level === L);
    if (lv.length > 0) mergeCells(lv, L * H - 0.3, 0.3);
  }
  for (let L = 0; L < 8; L++) {
    const lv = shell.caps.filter((c) => c.level === L);
    if (lv.length > 0) mergeCells(lv, (L + 1) * H - SHELL_ROOF_T, SHELL_ROOF_T);
  }
  // Walls: non-door panels merge into contiguous runs per (level, side, row)
  // — collision-wise a window wall IS a solid wall, so kind is irrelevant
  // here. Door walls emit individually (flanks + fixed header).
  const emitWallRuns = (walls: (ShellWall & { level?: number })[]): void => {
    const runs = new Map<string, { level: number; side: 0 | 1 | 2 | 3; cross: number; alongs: number[] }>();
    for (const wl of walls) {
      if (wl.kind === "door") { doorBox(wl, wl.level ?? 0); continue; }
      const level = wl.level ?? 0;
      const alongX = wl.side <= 1;
      const cross = alongX ? wl.z : wl.x;
      const along = alongX ? wl.x : wl.z;
      const key = `${level}:${wl.side}:${cross}`;
      const r = runs.get(key) ?? { level, side: wl.side, cross, alongs: [] };
      r.alongs.push(along);
      runs.set(key, r);
    }
    for (const key of [...runs.keys()].sort()) {
      const r = runs.get(key)!;
      const alongX = r.side <= 1;
      const inset = U / 2 - T / 2;
      const xs = r.alongs.sort((a, q) => a - q);
      let start = xs[0], prev = xs[0];
      const flush = (end: number): void => {
        const len = end - start + U;
        const mid = (start + end) / 2;
        const ex = alongX ? mid : r.cross + SIDE_DX[r.side] * inset;
        const ez = alongX ? r.cross + SIDE_DZ[r.side] * inset : mid;
        out.push({
          x: ex, y: r.level * H, z: ez,
          w: alongX ? len : T, h: H, d: alongX ? T : len, wall: true,
        });
      };
      for (let i = 1; i < xs.length; i++) {
        if (xs[i] - prev > U + 1e-6) { flush(prev); start = xs[i]; }
        prev = xs[i];
      }
      flush(prev);
    }
  };
  const doorBox = (wl: ShellWall, level: number): void => {
    // Doorway: two full-height flanks + a header over the opening. The header
    // bottom is FIXED at SHELL_DOOR_HEAD_M (not sill-relative) so the authored
    // door piece and this collision always agree across floor raises; the gap
    // below it is the walk-through contract (the floor slab is the sill).
    const ex = wl.x + SIDE_DX[wl.side] * (U / 2 - T / 2);
    const ez = wl.z + SIDE_DZ[wl.side] * (U / 2 - T / 2);
    const alongX = wl.side <= 1;
    const y0 = level * H;
    const flank = (U - SHELL_DOOR_W) / 2;
    const off = SHELL_DOOR_W / 2 + flank / 2;
    if (alongX) {
      out.push({ x: ex - off, y: y0, z: ez, w: flank, h: H, d: T, wall: true });
      out.push({ x: ex + off, y: y0, z: ez, w: flank, h: H, d: T, wall: true });
      out.push({ x: ex, y: y0 + SHELL_DOOR_HEAD_M, z: ez, w: SHELL_DOOR_W, h: H - SHELL_DOOR_HEAD_M, d: T, wall: true });
    } else {
      out.push({ x: ex, y: y0, z: ez - off, w: T, h: H, d: flank, wall: true });
      out.push({ x: ex, y: y0, z: ez + off, w: T, h: H, d: flank, wall: true });
      out.push({ x: ex, y: y0 + SHELL_DOOR_HEAD_M, z: ez, w: T, h: H - SHELL_DOOR_HEAD_M, d: SHELL_DOOR_W, wall: true });
    }
  };
  emitWallRuns([...shell.walls, ...shell.upperWalls]);
  // Stairs: one standable FULL-HEIGHT COLUMN per tread, floor → tread top
  // (2026-07-30 "stairs can be walked through from the back"): a thin tread
  // never pushes laterally (ROOF_STEP exempts its whole height), so the old
  // tread stack was a ghost from the sides/behind. A column's top IS the
  // tread line (climbing identical), while its body below the top 0.9 m band
  // pushes like any solid — the visual stair mass is now physically solid.
  // Same box count as thin treads: budget unchanged.
  const riser = H / SHELL_STAIR_TREADS;
  const step = SHELL_STAIR_RUN_M / SHELL_STAIR_TREADS;
  for (const st of shell.stairs) {
    const ax = ROT_DX[st.rot], az = ROT_DZ[st.rot];
    for (let k = 0; k < SHELL_STAIR_TREADS; k++) {
      const along = SHELL_STAIR_OFFSET_M - SHELL_STAIR_RUN_M / 2 + (k + 0.5) * step;
      const alongX = st.rot % 2 === 1;
      out.push({
        x: st.x + ax * along, y: st.level * H, z: st.z + az * along,
        w: alongX ? step : SHELL_STAIR_W_M, h: (k + 1) * riser,
        d: alongX ? SHELL_STAIR_W_M : step,
      });
    }
  }
  // Non-shell solids: the dome crown always; every level ≥ 1 module when the
  // archetype's massing is specialty (multiFloor false — P1 behaviour).
  for (const m of modules) {
    const solid = m.module === "mod-dome" || (!shell.multiFloor && m.level >= 1);
    if (!solid) continue;
    out.push({ x: m.x, y: m.y, z: m.z, w: m.wU * U, h: H, d: m.dU * U });
  }
  return out;
}

/** Cell (gx, gz) → metre-space placement for a wU×dU tile anchored there. */
function place(module: string, rot: 0 | 1 | 2 | 3, level: number,
  gx: number, gz: number, wU: number, dU: number,
  cellsW: number, cellsD: number): ModulePlacement {
  return {
    module, rot, level, gx, gz, wU, dU,
    x: (gx + (wU - 1) / 2 - (cellsW - 1) / 2) * MODULE_UNIT_M,
    y: level * LEVEL_H_M,
    z: (gz + (dU - 1) / 2 - (cellsD - 1) / 2) * MODULE_UNIT_M,
  };
}

/** True iff every cell of the wU×dU tile anchored at (gx, gz) is occupied and unclaimed. */
function fits(occ: Uint8Array, taken: Uint8Array, cellsW: number, cellsD: number,
  gx: number, gz: number, wU: number, dU: number): boolean {
  if (gx + wU > cellsW || gz + dU > cellsD) return false;
  for (let iz = 0; iz < dU; iz++) for (let ix = 0; ix < wU; ix++) {
    const i = (gz + iz) * cellsW + gx + ix;
    if (!occ[i] || taken[i]) return false;
  }
  return true;
}

function claim(taken: Uint8Array, cellsW: number,
  gx: number, gz: number, wU: number, dU: number): void {
  for (let iz = 0; iz < dU; iz++) for (let ix = 0; ix < wU; ix++) {
    taken[(gz + iz) * cellsW + gx + ix] = 1;
  }
}

/** In-place Fisher–Yates off the massing stream. */
function shuffle(rng: Rng, arr: number[]): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const t = arr[i]; arr[i] = arr[j]; arr[j] = t;
  }
}

/** Level-0 doorSide boundary cell nearest the footprint centre (tie: outermost,
 * then lowest index — fully deterministic). A boundary cell is an occupied cell
 * whose outward neighbour on `side` is missing or off-grid, so the door face is
 * always exposed even if a recipe's base shape is not a full rect. */
function doorCell(occ: Uint8Array, cellsW: number, cellsD: number,
  side: 0 | 1 | 2 | 3): [number, number] {
  let bestI = -1, bestDist = 0, bestOuter = 0;
  for (let gz = 0; gz < cellsD; gz++) for (let gx = 0; gx < cellsW; gx++) {
    if (!occ[gz * cellsW + gx]) continue;
    const nx = gx + SIDE_DX[side], nz = gz + SIDE_DZ[side];
    const exposed = nx < 0 || nx >= cellsW || nz < 0 || nz >= cellsD || !occ[nz * cellsW + nx];
    if (!exposed) continue;
    // doubled centre distance along the boundary axis keeps the math integral
    const dist = side <= 1 ? Math.abs(2 * gx - (cellsW - 1)) : Math.abs(2 * gz - (cellsD - 1));
    const outer = SIDE_DX[side] * gx + SIDE_DZ[side] * gz; // larger = further out
    if (bestI === -1 || dist < bestDist || (dist === bestDist && outer > bestOuter)) {
      bestI = gz * cellsW + gx; bestDist = dist; bestOuter = outer;
    }
  }
  // massing levels are never empty (rectMassing drops empty grids), so bestI ≥ 0
  return [bestI % cellsW, (bestI - (bestI % cellsW)) / cellsW];
}

/** Greedy largest-fit tiling per level over the massing grids. The level-0 door
 * tile is reserved FIRST as a guaranteed 1×1 `mod-box-door` on the doorSide
 * boundary (so the doorway is never absorbed into a hall/2×2 tile and the
 * 1.2 m door contract holds for every seed); remaining cells are tiled in
 * rng-shuffled anchor order for seam variety, trying each recipe footprint
 * largest-first in its listed then quarter-turned orientation. */
function tileMassing(rng: Rng, massing: Massing, recipe: Recipe,
  doorSide: 0 | 1 | 2 | 3): ModulePlacement[] {
  const { cellsW, cellsD } = massing;
  const topLevel = massing.levels.length - 1;
  const out: ModulePlacement[] = [];
  for (let level = 0; level < massing.levels.length; level++) {
    const occ = massing.levels[level];
    const taken = new Uint8Array(cellsW * cellsD);
    if (level === 0) {
      const [dgx, dgz] = doorCell(occ, cellsW, cellsD, doorSide);
      claim(taken, cellsW, dgx, dgz, 1, 1);
      out.push(place("mod-box-door", SIDE_ROT[doorSide], 0, dgx, dgz, 1, 1, cellsW, cellsD));
    }
    const anchors: number[] = [];
    for (let i = 0; i < occ.length; i++) if (occ[i]) anchors.push(i);
    shuffle(rng, anchors);
    for (const a of anchors) {
      if (taken[a]) continue;
      const gx = a % cellsW, gz = (a - gx) / cellsW;
      for (const [tw, td] of recipe.tileSizes) {
        // listed orientation first, then the quarter-turn when non-square
        const upright = fits(occ, taken, cellsW, cellsD, gx, gz, tw, td);
        const turned = !upright && tw !== td && fits(occ, taken, cellsW, cellsD, gx, gz, td, tw);
        if (!upright && !turned) continue;
        const wU = upright ? tw : td, dU = upright ? td : tw;
        claim(taken, cellsW, gx, gz, wU, dU);
        // moduleFor always sees the LOCAL footprint (tw×td); turned tiles carry
        // rot 1 and swapped grid extents so occupancy stays in grid axes.
        out.push(place(recipe.moduleFor(rng, tw, td, level, topLevel), upright ? 0 : 1,
          level, gx, gz, wU, dU, cellsW, cellsD));
        break;
      }
    }
  }
  // Dome crown (see Massing.domeCell): an extra 1×1 cap one level above the top
  // grid, emitted only if the named cell is actually occupied at the top level
  // so the "every module is supported" invariant survives a buggy recipe.
  const dc = massing.domeCell;
  if (dc !== undefined && massing.levels.length > 0 &&
      massing.levels[topLevel][dc[1] * cellsW + dc[0]] === 1) {
    out.push(place("mod-dome", 0, topLevel + 1, dc[0], dc[1], 1, 1, cellsW, cellsD));
  }
  return out;
}

/** The door contract: the level-0 door tile's outer-face centre. */
function doorFor(modules: ModulePlacement[], side: 0 | 1 | 2 | 3): BuildingPlan["door"] {
  let m = modules[0];
  for (const p of modules) if (p.level === 0 && p.module === "mod-box-door") { m = p; break; }
  return {
    x: m.x + SIDE_DX[side] * (m.wU * MODULE_UNIT_M) / 2,
    z: m.z + SIDE_DZ[side] * (m.dU * MODULE_UNIT_M) / 2,
    side, wM: 1.2, hM: 2.1,
  };
}

/** One AABB per placed module; y is the box BASE (module docs span [0, LEVEL_H_M]).
 * The doorway opening is NOT carved here — `plan.door` is the walk-through contract. */
function collisionBoxes(modules: ModulePlacement[]): LocalBox[] {
  return modules.map((m) => ({
    x: m.x, y: m.y, z: m.z,
    w: m.wU * MODULE_UNIT_M, h: LEVEL_H_M, d: m.dU * MODULE_UNIT_M,
  }));
}

// ── F2: plan height for collision (memoized) ─────────────────────────────────

/** buildingPlanHeightM memo — collision rebuilds fold per region and plans are
 * cheap but not free. Capped: when full it is RESET (cleared) wholesale; the
 * cache is a pure memo of a deterministic function, so eviction can never
 * change an answer. */
const planHeightCache = new Map<string, number>();
const PLAN_HEIGHT_CACHE_MAX = 4096;

/**
 * Rendered height (metres above the grounded base) of a building record's plan:
 * `max(box.y + box.h)` over `buildingPlan(...).collision`. This is what the
 * DEFAULT-ON procedural visuals actually build — towers overshoot the record's
 * height budget by design, landmark budgets clamp at the level cap — and it is
 * what the collision fold (settlementCollision.ts) uses for box height, so
 * solids coincide with the drawn massing.
 *
 * Runs the plan with `doorSide: 0`, which is SAFE for the height even though
 * doorSide DOES change tiling: reserving the door tile first shifts the greedy
 * tile seams (module ids/extents can differ per side). The max collision top
 * cannot differ, because collisionBoxes gives EVERY module box the span
 * [level·LEVEL_H_M, (level+1)·LEVEL_H_M] — so the max is decided solely by the
 * SET of occupied levels (plus the dome cap one level above the top grid), and
 * that set comes from recipe.massing + Massing.domeCell, which never see
 * doorSide. (Verified against tileMassing: it tiles every occupied cell of
 * every massing level, so each occupied level always yields ≥ 1 box; the dome
 * guard reads only top-grid occupancy.) That total-coverage guarantee — and so
 * this height's doorSide invariance — rests on every recipe's `tileSizes`
 * including the 1×1 tile: without a 1×1 fallback the greedy tiler could leave an
 * odd cell uncovered, dropping its box and breaking both claims. Pinned by the
 * doorSide-invariance sweep in buildings-plan.test.mts.
 *
 * Deterministic and pure (memo aside); pmath-ban safe (no trig, no Math.random).
 */
export function buildingPlanHeightM(archetype: BuildingArchetype, seed: number,
  wM: number, dM: number, hM: number): number {
  const key = `${archetype}:${seed}:${wM}:${dM}:${hM}`;
  const hit = planHeightCache.get(key);
  if (hit !== undefined) return hit;
  const plan = buildingPlan({ archetype, seed, wM, dM, hM, doorSide: 0 });
  let maxY = 0;
  for (const b of plan.collision) if (b.y + b.h > maxY) maxY = b.y + b.h;
  if (planHeightCache.size >= PLAN_HEIGHT_CACHE_MAX) planHeightCache.clear();
  planHeightCache.set(key, maxY);
  return maxY;
}

/**
 * Corner-occupancy trim pass — a PURE function of massing + modules that consumes
 * NO rng (draws from no layer stream; the stair is placed geometrically). Rules:
 *  - Parapets ring EXPOSED roof cells (occupied at L, nothing occupies the cell
 *    above). A side is OPEN — and gets a `trim-parapet-edge` — iff the SAME-level
 *    neighbour is unoccupied (so the parapet stops where the roof continues or a
 *    higher wall rises; a solo cell → 4 edges). Two adjacent open sides form a
 *    convex corner → `trim-parapet-corner`.
 *    Cells whose occupying module is `mod-tower-head`, and cells capped by a
 *    `mod-dome` one level up, get NO parapets: the head is shorter than
 *    LEVEL_H_M (a standard-height ring would float) and a dome has no roof
 *    edge — both carry their own edge treatment in the kit doc (F1.2).
 *  - `trim-skirt` on every level-0 boundary side (outward neighbour unoccupied)
 *    EXCEPT the door module's door face — a rubble skirt must never cross the
 *    doorway (F1.1, the phase-2 seamless-entry prerequisite). The door face is
 *    recovered from the door module's rot: SIDE_ROT is an involution, so the
 *    side→rot table [0,2,1,3] inverts to itself.
 *  - `trim-stair` where level L+1 is inset over exposed L roof (a step), one per
 *    transition, on the step face of the upper-storey cell nearest the door cell.
 *
 * Placement anchors match each committed trim geometry's LOCAL origin: the
 * parapet-edge bar is pre-offset to the cell edge, so it anchors at the cell
 * CENTRE; the centred corner/skirt/stair geometries anchor at the corner / edge
 * midpoint (cell centre + a half-unit toward the open side). rot = SIDE_ROT.
 */
export function trimPass(massing: Massing, modules: ModulePlacement[]): TrimPlacement[] {
  const { cellsW, cellsD, levels } = massing;
  const HALF = MODULE_UNIT_M / 2;
  const occ = (L: number, gx: number, gz: number): boolean =>
    L >= 0 && L < levels.length && gx >= 0 && gx < cellsW && gz >= 0 && gz < cellsD &&
    levels[L][gz * cellsW + gx] === 1;
  const cx = (gx: number): number => (gx - (cellsW - 1) / 2) * MODULE_UNIT_M;
  const cz = (gz: number): number => (gz - (cellsD - 1) / 2) * MODULE_UNIT_M;
  const trims: TrimPlacement[] = [];

  // Cell → occupying module id (multi-cell tiles cover every claimed cell):
  // drives the door-face skirt skip and the tower-head/dome parapet skip.
  const modAt = new Map<string, string>();
  for (const m of modules) {
    for (let iz = 0; iz < m.dU; iz++) for (let ix = 0; ix < m.wU; ix++) {
      modAt.set(`${m.level}:${m.gx + ix}:${m.gz + iz}`, m.module);
    }
  }
  const door = modules.find((m) => m.level === 0 &&
    (m.module === "mod-box-door" || m.module === "mod-box-door-hollow"));
  // Door FACE from the door module's rot (SIDE_ROT is its own inverse).
  const doorFace = door ? SIDE_ROT[door.rot] : -1;

  for (let L = 0; L < levels.length; L++) {
    const roofY = (L + 1) * LEVEL_H_M;
    for (let gz = 0; gz < cellsD; gz++) for (let gx = 0; gx < cellsW; gx++) {
      if (!occ(L, gx, gz)) continue;
      // Ground skirt: every level-0 boundary side EXCEPT the door face, y = 0.
      if (L === 0) {
        for (let s = 0; s < 4; s++) {
          if (door && gx === door.gx && gz === door.gz && s === doorFace) continue;
          if (!occ(0, gx + SIDE_DX[s], gz + SIDE_DZ[s])) {
            trims.push({ piece: "trim-skirt", rot: SIDE_ROT[s], y: 0,
              x: cx(gx) + SIDE_DX[s] * HALF, z: cz(gz) + SIDE_DZ[s] * HALF });
          }
        }
      }
      // Parapets only on exposed roof cells — nothing occupies the cell above.
      if (occ(L + 1, gx, gz)) continue;
      // No parapets on tower heads or under dome caps (see header, F1.2).
      if (modAt.get(`${L}:${gx}:${gz}`) === "mod-tower-head" ||
          modAt.get(`${L + 1}:${gx}:${gz}`) === "mod-dome") continue;
      const open = (s: number): boolean => !occ(L, gx + SIDE_DX[s], gz + SIDE_DZ[s]);
      for (let s = 0; s < 4; s++) {
        if (open(s)) {
          trims.push({ piece: "trim-parapet-edge", rot: SIDE_ROT[s],
            x: cx(gx), y: roofY, z: cz(gz) });
        }
      }
      for (const [a, b] of CORNER_PAIRS) {
        if (open(a) && open(b)) {
          trims.push({ piece: "trim-parapet-corner", rot: 0, y: roofY,
            x: cx(gx) + (SIDE_DX[a] + SIDE_DX[b]) * HALF,
            z: cz(gz) + (SIDE_DZ[a] + SIDE_DZ[b]) * HALF });
        }
      }
    }
  }

  // Stairs: one per inset step, on the upper-storey step face nearest the door.
  const dgx = door ? door.gx : 0, dgz = door ? door.gz : 0;
  for (let L = 0; L + 1 < levels.length; L++) {
    let best: { gx: number; gz: number; s: number; dist: number; i: number } | null = null;
    for (let gz = 0; gz < cellsD; gz++) for (let gx = 0; gx < cellsW; gx++) {
      if (!occ(L + 1, gx, gz)) continue;
      for (let s = 0; s < 4; s++) {
        const nx = gx + SIDE_DX[s], nz = gz + SIDE_DZ[s];
        // step face: an upper-storey wall here overlooks exposed lower roof
        if (occ(L + 1, nx, nz) || !occ(L, nx, nz)) continue;
        const ddx = gx - dgx, ddz = gz - dgz;
        const dist = ddx * ddx + ddz * ddz;
        const i = gz * cellsW + gx;
        if (!best || dist < best.dist || (dist === best.dist && i < best.i)) {
          best = { gx, gz, s, dist, i };
        }
      }
    }
    if (best) {
      trims.push({ piece: "trim-stair", rot: SIDE_ROT[best.s], y: (L + 1) * LEVEL_H_M,
        x: cx(best.gx) + SIDE_DX[best.s] * HALF, z: cz(best.gz) + SIDE_DZ[best.s] * HALF });
    }
  }
  return trims;
}

/**
 * Greeble pass — weighted socket fill + at-most-one wildcard, both drawn from
 * their OWN layer streams (`rng` = LAYER_GREEBLE, `wildRng` = LAYER_WILDCARD)
 * so this pass is provably independent of massing/trims. NO trig; sockets are
 * COMPUTED from the massing grid in unit space (no kit-doc lookup):
 *
 *  - roof   = exposed cell centre (occupied at L, nothing above), y = (L+1)·H,
 *             rot 0. The `Massing.domeCell` is NOT a roof socket — the dome cap
 *             sits on that plane — and it is excluded from wildcard placement
 *             (the wildcard lands on the next-nearest exposed roof cell).
 *  - wall   = exterior face of an occupied cell (same-level neighbour empty),
 *             y = L·H + H/2, at the cell edge, rot = SIDE_ROT[side]. Excluded:
 *             (a) the "no awning under an overhang" face — the neighbour airspace
 *             in front of the face is occupied at L+1; (b) the level-0 door face.
 *  - ground = level-0 exterior face pushed 0.9 m outward, y = 0, rot = SIDE_ROT.
 *             The door face is skipped so nothing blocks the doorway.
 *
 * Sockets are visited in a FIXED canonical order (level, gz, gx, then within a
 * cell: ground sides, wall sides, roof) — determinism needs a total order and
 * nothing here shuffles. Each socket rolls `rng() < fillProb[type]`; on a hit a
 * weighted pick honours per-group quotas. The wildcard rolls `wildRng() < 1/8`
 * once, weighted-picks, and lands on the exposed roof cell nearest the footprint
 * centre (squared unit distance; ties → higher level, then row-major). The
 * wildcard is also pushed into `greebles` so it carries a placement to bake.
 */
export function greeblePass(
  rng: Rng, wildRng: Rng, massing: Massing, modules: ModulePlacement[],
  recipe: Recipe, doorSide: number,
): { greebles: GreeblePlacement[]; wildcard: string | null } {
  const { cellsW, cellsD, levels } = massing;
  const HALF = MODULE_UNIT_M / 2;
  const occ = (L: number, gx: number, gz: number): boolean =>
    L >= 0 && L < levels.length && gx >= 0 && gx < cellsW && gz >= 0 && gz < cellsD &&
    levels[L][gz * cellsW + gx] === 1;
  const cx = (gx: number): number => (gx - (cellsW - 1) / 2) * MODULE_UNIT_M;
  const cz = (gz: number): number => (gz - (cellsD - 1) / 2) * MODULE_UNIT_M;

  const door = modules.find((m) => m.level === 0 &&
    (m.module === "mod-box-door" || m.module === "mod-box-door-hollow"));
  const dgx = door ? door.gx : -1, dgz = door ? door.gz : -1;

  // F1.3: the dome cap sits ON the domeCell's roof plane, so that cell's roof
  // socket (and the wildcard) must never fill — a tank/vent would intersect
  // the dome. The socket is skipped BEFORE its fill roll, which shifts greeble
  // draws ONLY for plans that carry a domeCell (today: lab, ~35% of seeds);
  // every other archetype's greeble stream is byte-identical (pinned in test).
  // tileMassing's dome guard (occupied top cell) matches this exactly: an
  // occupied domeCell always means a dome module was emitted above it.
  const topLevel = levels.length - 1;
  const dome = massing.domeCell;
  const isDomeCell = (L: number, gx: number, gz: number): boolean =>
    dome !== undefined && L === topLevel && gx === dome[0] && gz === dome[1];

  const greebles: GreeblePlacement[] = [];
  const used: Record<string, number> = {};

  /** Roll fill, then a quota-aware weighted pick. Consumes one rng() for the
   * roll and (on a hit with candidates) one more for the pick — a fixed draw
   * pattern so the stream stays deterministic. */
  const fillSocket = (type: GreebleEntry["socket"],
    x: number, y: number, z: number, rot: 0 | 1 | 2 | 3): void => {
    if (!(rng() < recipe.fillProb[type])) return;
    const avail = recipe.greebles.filter((e) => e.socket === type &&
      (e.quotaGroup === undefined ||
        (used[e.quotaGroup] ?? 0) < (recipe.quotas[e.quotaGroup] ?? 0)));
    if (avail.length === 0) return;
    let total = 0; for (const e of avail) total += e.weight;
    let r = rng() * total;
    let chosen = avail[avail.length - 1];
    for (const e of avail) { r -= e.weight; if (r < 0) { chosen = e; break; } }
    if (chosen.quotaGroup !== undefined) {
      used[chosen.quotaGroup] = (used[chosen.quotaGroup] ?? 0) + 1;
    }
    greebles.push({ greeble: chosen.greeble, x, y, z, rot });
  };

  for (let L = 0; L < levels.length; L++) {
    for (let gz = 0; gz < cellsD; gz++) for (let gx = 0; gx < cellsW; gx++) {
      if (!occ(L, gx, gz)) continue;
      // Ground props: level-0 exterior faces, pushed 0.9 m out (skip door face).
      if (L === 0) {
        for (let s = 0; s < 4; s++) {
          if (occ(0, gx + SIDE_DX[s], gz + SIDE_DZ[s])) continue;
          if (gx === dgx && gz === dgz && s === doorSide) continue;
          fillSocket("ground", cx(gx) + SIDE_DX[s] * (HALF + 0.9), 0,
            cz(gz) + SIDE_DZ[s] * (HALF + 0.9), SIDE_ROT[s]);
        }
      }
      // Wall props: exterior faces, skipping overhang faces and the door face.
      for (let s = 0; s < 4; s++) {
        const nx = gx + SIDE_DX[s], nz = gz + SIDE_DZ[s];
        if (occ(L, nx, nz)) continue;                 // interior face
        if (occ(L + 1, nx, nz)) continue;             // no awning under an overhang
        if (L === 0 && gx === dgx && gz === dgz && s === doorSide) continue;
        fillSocket("wall", cx(gx) + SIDE_DX[s] * HALF, L * LEVEL_H_M + LEVEL_H_M / 2,
          cz(gz) + SIDE_DZ[s] * HALF, SIDE_ROT[s]);
      }
      // Roof props: exposed cell centres (nothing occupies the cell above),
      // never the dome cell — skipped before any roll (see isDomeCell above).
      if (!occ(L + 1, gx, gz) && !isDomeCell(L, gx, gz)) {
        fillSocket("roof", cx(gx), (L + 1) * LEVEL_H_M, cz(gz), 0);
      }
    }
  }

  // Wildcard: p = 1/8, weighted, on the exposed roof cell nearest footprint centre.
  let wildcard: string | null = null;
  if (wildRng() < 0.125 && recipe.wildcards.length > 0) {
    let best: { L: number; gx: number; gz: number; dist: number } | null = null;
    for (let L = 0; L < levels.length; L++) {
      for (let gz = 0; gz < cellsD; gz++) for (let gx = 0; gx < cellsW; gx++) {
        if (!occ(L, gx, gz) || occ(L + 1, gx, gz)) continue;
        if (isDomeCell(L, gx, gz)) continue; // F1.3: next-nearest roof cell instead
        const ddx = 2 * gx - (cellsW - 1), ddz = 2 * gz - (cellsD - 1);
        const dist = ddx * ddx + ddz * ddz;
        if (!best || dist < best.dist || (dist === best.dist && L > best.L)) {
          best = { L, gx, gz, dist };
        }
      }
    }
    if (best) {
      let total = 0; for (const w of recipe.wildcards) total += w.weight;
      let r = wildRng() * total;
      let pick = recipe.wildcards[recipe.wildcards.length - 1];
      for (const w of recipe.wildcards) { r -= w.weight; if (r < 0) { pick = w; break; } }
      wildcard = pick.greeble;
      greebles.push({ greeble: pick.greeble, x: cx(best.gx), y: (best.L + 1) * LEVEL_H_M,
        z: cz(best.gz), rot: 0 });
    }
  }

  return { greebles, wildcard };
}

/**
 * Record (yaw, doorYaw) → the building-local side the plan generator should put
 * the door on: 0 = +z (D), 1 = −z, 2 = +x (W), 3 = −x. Shared so phase 2's
 * server-side plan regeneration derives the SAME side the client bake does.
 *
 * Derivation (the SAME dot-product face pick the legacy kit path uses,
 * renderer.ts ~1229-1238, collapsed to closed form): with W = t1·cos(yaw) +
 * t2·sin(yaw), D = W × up and doorDir = t1·cos(doorYaw) + t2·sin(doorYaw),
 * the four face-normal dots reduce to functions of r = doorYaw − yaw:
 *   +D → −sin r,  −D → +sin r,  +W → cos r,  −W → −cos r.
 * Argmax with FIRST-WINS ties — the identical order and strict `>` the legacy
 * loop uses, so both paths pick the same face for every record. Trig routes
 * through pmath (bit-identical on every engine — the whole point of sharing).
 */
export function planDoorSide(yaw: number, doorYaw: number): 0 | 1 | 2 | 3 {
  const r = doorYaw - yaw;
  const sr = pmath.sin(r), cr = pmath.cos(r);
  const dots = [-sr, sr, cr, -cr];
  let best = 0;
  for (let i = 1; i < 4; i++) if (dots[i] > dots[best]) best = i;
  return best as 0 | 1 | 2 | 3;
}
